import { CAPS } from '@/constants';
import { MOET_CONSTANTS } from 'moet-core';

const { WIDGET_TYPE } = MOET_CONSTANTS;

export const ALL_CAPS = {
  getCountries: {
    cap: CAPS.V2_COUNTRIES_GET,
    key: 'getCountries',
  },
  getEntityTypes: {
    cap: CAPS.V2_ENTITY_TYPES_GET,
    key: 'getEntityTypes',
  },
  getMerchant: {
    cap: CAPS.V2_MERCHANTS_GET,
    key: 'getMerchant',
  },
  getOptions: {
    cap: CAPS.V2_OPTIONS_GET,
    key: 'getOptions',
  },
  getPartners: {
    cap: CAPS.V2_PARTNERS_GET,
    key: 'getPartners',
  },
  getOrganizationEntityTypes: {
    cap: CAPS.V2_ORGANIZATION_ENTITY_TYPES_GET,
    key: 'getOrganizationEntityTypes',
  },
  getPlatforms: {
    cap: CAPS.V2_PLATFORMS_GET,
    key: 'getPlatforms',
  },
  getPreAssessmentData: {
    cap: CAPS.V2_PRE_ASSESSMENT_GET,
    key: 'getPreAssessmentData',
    catch: error => {
      // 422 is expected here and doesn't need to go to sentry
      // This is only used on the app.js file, it's not a widely available
      // feature to handle capability errors
      if (error?.response?.status !== 422) {
        throw error;
      }
    },
  },
  getPreAssessmentUnderwritingStatus: {
    cap: CAPS.V2_PRE_ASSESSMENT_UNDERWRITING_STATUS_GET,
    key: 'getPreAssessmentUnderwritingStatus',
  },
  getProducts: {
    cap: CAPS.V2_PRODUCTS_GET,
    key: 'getProducts',
  },
  getRecollectionData: {
    cap: CAPS.V2_MERCHANTS_RECOLLECTION_GET,
    key: 'getRecollectionData',
  },
  getExperiments: {
    cap: CAPS.EXPERIMENTS_GET,
    key: 'getExperiments',
  },
  getFeaturesConfig: {
    cap: CAPS.FEATURE_CONFIG_GET,
    key: 'getFeaturesConfig',
  },
  listDocuments: {
    cap: CAPS.V2_DOC_LIST,
    key: 'listDocuments',
  },
  getRequiredDocuments: {
    cap: CAPS.V2_DOC_TOKEN_INFO,
    key: 'getRequiredDocuments',
  },
  getBpc: {
    cap: CAPS.BPC_GET,
    key: 'getBpc',
  },
  patchBpc: {
    cap: CAPS.BPC_PATCH,
    key: 'patchBpc',
  },
};

const withCaps = caps => caps.reduce((cs, c) => ({ ...cs, [c.key]: c.cap }), {});

// Caps that willl be executed on every flow
const BASE_CAPS = [ALL_CAPS.getExperiments, ALL_CAPS.getFeaturesConfig];

const PARTNERS_CAPS = [...BASE_CAPS, ALL_CAPS.getPartners];

const SIGNUP_EXEC = [
  ...BASE_CAPS,
  ALL_CAPS.getCountries,
  ALL_CAPS.getPlatforms,
  ALL_CAPS.getOptions,
];
const SIGNUP_INIT = [
  ...BASE_CAPS,
  ALL_CAPS.getCountries,
  ALL_CAPS.getPlatforms,
  ALL_CAPS.getOptions,
];

const RECOLLECTION_EXEC = [...BASE_CAPS, ALL_CAPS.getOptions, ALL_CAPS.getRecollectionData];
const RECOLLECTION_INIT = [...BASE_CAPS, ALL_CAPS.getOptions];

const DOCUMENT_UPLOAD_EXEC = [...BASE_CAPS, ALL_CAPS.getOptions, ALL_CAPS.getRequiredDocuments];
const DOCUMENT_UPLOAD_INIT = [...BASE_CAPS, ALL_CAPS.getOptions];

const BPC_POC_EXEC = [...BASE_CAPS, ALL_CAPS.getOptions];
const BPC_POC_INIT = [...BASE_CAPS, ALL_CAPS.getOptions];

const KYC_EXEC = [
  ...BASE_CAPS,
  ALL_CAPS.getCountries,
  ALL_CAPS.getEntityTypes,
  ALL_CAPS.getMerchant,
  ALL_CAPS.listDocuments,
  ALL_CAPS.getOptions,
  ALL_CAPS.getOrganizationEntityTypes,
  ALL_CAPS.getPlatforms,
  ALL_CAPS.getProducts,
];
const KYC_INIT = [
  ...BASE_CAPS,
  ALL_CAPS.getCountries,
  ALL_CAPS.getEntityTypes,
  ALL_CAPS.getOptions,
  ALL_CAPS.getOrganizationEntityTypes,
  ALL_CAPS.getPlatforms,
];

const PRE_ASSESSMENT_EXEC = [
  ...BASE_CAPS,
  ALL_CAPS.getCountries,
  ALL_CAPS.getEntityTypes,
  ALL_CAPS.getMerchant,
  ALL_CAPS.getOptions,
  ALL_CAPS.getOrganizationEntityTypes,
  ALL_CAPS.getPlatforms,
  ALL_CAPS.getPreAssessmentData,
  ALL_CAPS.getPreAssessmentUnderwritingStatus,
  ALL_CAPS.getProducts,
];
const PRE_ASSESSMENT_INIT = [
  ...BASE_CAPS,
  ALL_CAPS.getCountries,
  ALL_CAPS.getEntityTypes,
  ALL_CAPS.getOptions,
  ALL_CAPS.getOrganizationEntityTypes,
  ALL_CAPS.getPlatforms,
];

export const getExecCaps = (
  widgetType = WIDGET_TYPE.UNMANAGED_KYC,
  isSignup = false,
  isPsp = false,
) =>
  withCaps(
    (() => {
      if (isSignup) {
        return SIGNUP_EXEC;
      }
      if (isPsp) {
        return PARTNERS_CAPS;
      }
      switch (widgetType) {
        case WIDGET_TYPE.DOCUMENT_UPLOAD:
          return DOCUMENT_UPLOAD_EXEC;
        case WIDGET_TYPE.BPC_POC:
          return BPC_POC_EXEC;
        case WIDGET_TYPE.RECOLLECTION_KYC:
          return RECOLLECTION_EXEC;
        case WIDGET_TYPE.MANAGED_RISK:
        case WIDGET_TYPE.MANAGED_SME_RISK:
          return PRE_ASSESSMENT_EXEC;
        case WIDGET_TYPE.UNMANAGED_KYC:
        case WIDGET_TYPE.MANAGED_KYC:
        case WIDGET_TYPE.MANAGED_SME_KYC:
        case WIDGET_TYPE.NATIVE_KYC:
        default:
          return KYC_EXEC;
      }
    })(),
  );

export const getInitCaps = (
  widgetType = WIDGET_TYPE.UNMANAGED_KYC,
  isSignup = false,
  isPsp = false,
) =>
  withCaps(
    (() => {
      if (isSignup) {
        return SIGNUP_INIT;
      }
      if (isPsp) {
        return PARTNERS_CAPS;
      }
      switch (widgetType) {
        case WIDGET_TYPE.DOCUMENT_UPLOAD:
          return DOCUMENT_UPLOAD_INIT;
        case WIDGET_TYPE.BPC_POC:
          return BPC_POC_INIT;
        case WIDGET_TYPE.RECOLLECTION_KYC:
          return RECOLLECTION_INIT;

        case WIDGET_TYPE.MANAGED_RISK:
        case WIDGET_TYPE.MANAGED_SME_RISK:
          return PRE_ASSESSMENT_INIT;

        case WIDGET_TYPE.UNMANAGED_KYC:
        case WIDGET_TYPE.MANAGED_KYC:
        case WIDGET_TYPE.MANAGED_SME_KYC:
        case WIDGET_TYPE.NATIVE_KYC:
        default:
          return KYC_INIT;
      }
    })(),
  );
